<script>
import { Scatter } from "vue-chartjs";
import "chartjs-plugin-zoom";
import moment from 'moment';

moment.locale('cs');

export default {
  extends: Scatter,
  props: {
    chartData: {
      type: Object,
      default: null
    },
    phUnit: {
      type: String,
      default: null
    }
  },
  mounted() {
    this.addPlugin({
      beforeDraw(chart) {
        const ctx = chart.chart.ctx;
        const { width, height } = chart.chart;
        ctx.save();
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.restore();
      },
      afterDraw(chart) {
        const thresholds = chart.config.options.customThresholds;
        if (!Array.isArray(thresholds) || thresholds.length === 0) return;

        const yScale = Object.values(chart.scales).find(s => s.id.startsWith('y-') || s.id.startsWith('y-axis'));
        if (!yScale) return;

        const ctx = chart.chart.ctx;
        ctx.save();
        ctx.strokeStyle = 'rgba(0,0,0,0.7)';
        ctx.lineWidth = 1;
        ctx.setLineDash([5, 5]);

        thresholds.forEach((value) => {
          if (value == null || isNaN(value)) return;
          const y = yScale.getPixelForValue(value);
          if (y < yScale.top || y > yScale.bottom) return;

          ctx.beginPath();
          ctx.moveTo(chart.chartArea.left, y);
          ctx.lineTo(chart.chartArea.right, y);
          ctx.stroke();
        });

        ctx.restore();
      },
      beforeUpdate(chart) {
        const xScale = Object.values(chart.scales).find(s => s.options && s.options.type === 'time');
        if (!xScale) return;

        const min = xScale.min;
        const max = xScale.max;
        if (min == null || max == null) return;

        const totalHours = (max - min) / (1000 * 60 * 60);
        let stepSize = 1;
        if (totalHours > 24 * 20) {
          stepSize = 12;
        } else if (totalHours > 24 * 7) {
          stepSize = 6;
        } else if (totalHours > 24 * 3) {
          stepSize = 3;
        } else {
          stepSize = 1;
        }

        xScale.options.time.unit = 'hour';
        xScale.options.time.stepSize = stepSize;
      }
    });
  },
  watch: {
    chartData: {
      handler() {
        if (!this.chartData || !this.chartData.datasets.length) {
          const chart = this.$data._chart;
          if (chart) {
            chart.data.datasets = [];
            chart.update(0);
          }
          return;
        }

        this.renderChart(this.chartData, {
          maintainAspectRatio: false,
          responsive: true,
          scales: {
            xAxes: [
              {
                type: "time",
                distribution: "linear",
                time: {
                  unit: "hour",
                  stepSize: 1,
                  displayFormats: {
                    millisecond: 'DD.MM HH:mm:ss',
                    second: "DD.MM HH:mm:ss",
                    minute: "DD.MM HH:mm:ss",
                    hour: "DD.MM HH:mm",
                    day: "DD.MM",
                    week: 'DD.MM',
                    month: 'MM.YYYY'
                  }
                },
                ticks: {
                  autoSkip: false,
                  maxRotation: 45,
                  minRotation: 30
                },
                scaleLabel: {
                  display: true,
                  labelString: this.$i18n.t("project.graphViewer.axisX")
                }
              }
            ],
            yAxes: [
              {
                type: "linear",
                position: "left",
                scaleLabel: {
                  display: true,
                  labelString: `${this.$i18n.t("project.graphViewer.axisY")} (${
                    this.phUnit
                  })`
                }
              }
            ]
          },
          tooltips: {
            callbacks: {
              title: function(tooltipItem) {
                const date = moment(tooltipItem[0].label).format('DD.MM HH:mm:ss');
                return date;
              },
              label: function(tooltipItem) {
                const value = tooltipItem.value;
                return value;
              }
            }
          },
          plugins: {
            zoom: {
              pan: {
                enabled: false,
                mode: "xy",
                onPan: function(arg) {
                  const chart = arg.chart || arg;
                  chart.options.scales.xAxes[0].ticks.callback = ((value, index, values) => {
                    const tick = values[index];
                    const ts = (tick && typeof tick === "object" && tick.value != null) ? tick.value : tick;
                    const n = Number(ts);
                    const m = n < 1e12 ? moment.unix(n) : moment(n);
                    return m.format("DD.MM HH:mm:ss");
                  });
                  chart.update(0);
                },
              },
              zoom: {
                enabled: false,
                drag: false,
                mode: "xy",
                speed: 0.05,
                onZoom: function(arg) {
                  const chart = arg.chart || arg;
                  chart.options.scales.xAxes[0].ticks.callback = ((value, index, values) => {
                    const tick = values[index];
                    const ts = (tick && typeof tick === "object" && tick.value != null) ? tick.value : tick;
                    const n = Number(ts);
                    const m = n < 1e12 ? moment.unix(n) : moment(n);
                    return m.format("DD.MM HH:mm:ss");
                  });
                  chart.update(0);
                }
              }
            }
          }
        });
      },
      deep: true
    }
  },
  methods: {
    setYRange(min, max) {
      const chart = this.$data._chart;
      if (!chart) return;
      chart.options.scales.yAxes[0].ticks.min = min;
      chart.options.scales.yAxes[0].ticks.max = max;
      chart.update();
    },
    clearYRange() {
      const chart = this.$data._chart;
      if (!chart) return;
      delete chart.options.scales.yAxes[0].ticks.min;
      delete chart.options.scales.yAxes[0].ticks.max;
      chart.update();
    },
    resetZoom(keepRange = false) {
      const chart = this.$data._chart;
      if (!chart || !chart.resetZoom) return;

      let yMin = chart.options.scales.yAxes[0].ticks.min;
      let yMax = chart.options.scales.yAxes[0].ticks.max;

      chart.resetZoom();
      // For now keep the keepRange to false and see the users input how does it work
      if (keepRange && (yMin !== undefined || yMax !== undefined)) {
        chart.options.scales.yAxes[0].ticks.min = yMin;
        chart.options.scales.yAxes[0].ticks.max = yMax;
        chart.update();
      }
    },
    toggleZoom(enable) {
      const chart = this.$data._chart;
      if (!chart) return;
      chart.options.plugins.zoom.zoom.enabled = enable;
      chart.options.plugins.zoom.pan.enabled = enable;
      chart.update();
    }
  }
};
</script>
